<script lang="ts">
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';

  // Seven layers from spec §2 — reading bottom-up (closest to the model first)
  // The visual story: the model sits underneath; "your AI" sits on top;
  // everything in between is what takes 6-12 months and $500K to build.
  const layers = [
    { name: 'User surfaces', detail: 'Hub, widgets, Teams/Slack, custom API clients' },
    { name: 'Governance & audit', detail: 'Approval flows, audit trail, policy enforcement' },
    { name: 'Tool integration', detail: 'Connectors, MCP, OpenAPI, private network access' },
    { name: 'Guardrails & CRF', detail: 'Content filtering, PII detection, action constraint' },
    { name: 'Model orchestration', detail: 'Routing, scoping, per-agent configuration' },
    { name: 'Identity & auth', detail: 'Entra ID, SSO, per-user permission scoping' },
    { name: 'Deployment architecture', detail: 'On-tenant cloud, private endpoints, residency' },
  ];
</script>

<section id="the-gap" class="bg-[#0d0d0d] relative">
  <GridLines variant="dark" />

  <GridContent>
    <GridInner>
      <div class="py-[80px] lg:py-[110px]">
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-[80px]">
          <!-- LEFT: prose -->
          <div class="max-w-[540px]">
            <p class="font-mono text-[12px] uppercase tracking-[0.12em] text-[#ee6018] mb-6">
              02 · The Gap
            </p>
            <h2 class="font-display font-[300] text-[40px] md:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.035em] text-white mb-10">
              The model works. Getting it to work <em class="italic font-[250] text-white">your way</em> is the hard part.
            </h2>

            <div class="flex flex-col gap-6">
              <p class="font-body font-light text-[17px] leading-[1.55] text-[#B8B8B8]">
                Enterprise AI projects fail at 50%. Not because the intelligence isn't there — because the system around it is incomplete. Skip any single layer and you're stuck building infrastructure instead of building your AI.
              </p>

              <div class="my-2 border-l-2 border-[#ee6018] pl-6 py-2">
                <p class="font-display font-[300] text-[22px] lg:text-[26px] leading-[1.25] tracking-[-0.02em] text-white">
                  The model is an API key away. That was never the hard part.
                </p>
              </div>

              <p class="font-body font-light text-[17px] leading-[1.55] text-[#B8B8B8]">
                The hard part is wiring it all together — the right model with the right data and the right tools at the right time — and doing it safely, in a way your security team will approve and your users will actually adopt.
              </p>
              <p class="font-body font-light text-[17px] leading-[1.55] text-white">
                Today, getting AI that works the way your business works costs $500K+ and takes 6-12 months before you can even start customizing. We think that's broken.
              </p>

              <a href="/why-corral#the-thesis" class="inline-flex items-center gap-2 font-mono text-[13px] text-[#ee6018] hover:text-white transition-colors mt-2 self-start">
                Read the thesis
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <!-- RIGHT: seven-layer stack -->
          <div class="flex flex-col items-center lg:items-stretch justify-start lg:py-4">
            <!-- Top label -->
            <div class="flex items-end justify-between mb-4 max-w-[440px] lg:max-w-none w-full">
              <div>
                <p class="font-mono text-[10px] uppercase tracking-[0.14em] text-[#878787] mb-1">What you're trying to ship</p>
                <p class="font-display text-[22px] lg:text-[26px] text-white tracking-tight">Your AI.</p>
              </div>
              <span class="font-mono text-[11px] text-[#878787]">↑</span>
            </div>

            <!-- Stack of 7 layers -->
            <div class="flex flex-col gap-[1px] bg-[#333335] border border-[#333335] max-w-[440px] lg:max-w-none w-full">
              {#each layers as layer, i}
                <div
                  class="stack-layer bg-[#171719] flex items-center justify-between px-5 py-[14px] relative group"
                  style="--i: {i};"
                >
                  <div class="flex items-center gap-4">
                    <span class="font-mono text-[10px] text-[#878787] tabular-nums w-[18px]">
                      0{i + 1}
                    </span>
                    <span class="font-display font-[300] text-[15px] lg:text-[16px] text-white">
                      {layer.name}
                    </span>
                  </div>
                  <span class="font-mono text-[11px] text-[#878787] hidden sm:block">
                    {layer.detail}
                  </span>
                </div>
              {/each}
            </div>

            <!-- Bottom label -->
            <div class="flex items-start justify-between mt-4 max-w-[440px] lg:max-w-none w-full">
              <div>
                <p class="font-mono text-[10px] uppercase tracking-[0.14em] text-[#878787] mb-1">What everyone else sells as "AI"</p>
                <p class="font-display text-[22px] lg:text-[26px] text-white tracking-tight">The model.</p>
              </div>
              <span class="font-mono text-[11px] text-[#878787]">↓</span>
            </div>

            <!-- Annotation -->
            <div class="mt-8 max-w-[440px] lg:max-w-none w-full">
              <p class="font-mono text-[12px] text-[#ee6018] leading-[1.5] border-t border-[#333335] pt-4">
                Skip any one layer — stuck building infrastructure instead of building your AI.
              </p>
            </div>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<style>
  /* Subtle staggered reveal so the stack feels built, not printed. */
  .stack-layer {
    animation: stack-reveal 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: calc(var(--i) * 60ms);
  }

  @keyframes stack-reveal {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .stack-layer {
      animation: none;
    }
  }
</style>
